import {
	BN,
	BigNum,
	MMOraclePriceData,
	OraclePriceData,
	ZERO,
} from '@velocity-exchange/sdk';
import { MarketId, MarketKey } from '@velocity-exchange/common';
import { useMemo } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import {
	LiveMarkSnapshot,
	LiveMmOracleSnapshot,
	LiveOracleSnapshot,
	LivePriceSource,
	MarketLiveUpdate,
} from './types';

/**
 * Implementation of the market live-state module (oracle + mark + mmOracle
 * price). See ./index.ts for the interface and ui/docs/velocity-migration-followups.md
 * for the migration plan.
 *
 * Internal design:
 * - A synchronous per-source cache (`pricesSourceCache`) is updated immediately on
 *   every publish. Imperative getters read it, so non-React consumers (tx
 *   prep, data transforms) always see the freshest value with zero throttle.
 * - A Zustand store (`useMarketPricesStore`) is flushed from the
 *   cache at most every FLUSH_INTERVAL_MS. React hooks read it, so render
 *   frequency is bounded regardless of transport chattiness. (This replaces
 *   the ad-hoc "local immer state + useInterval flush" pattern that used to
 *   live inside useSyncOracleDataWithRpc.)
 * - Freshness arbitration across sources happens in ONE place (selectBestOracle /
 *   selectBestMark below). No consumer ever compares transports again.
 */

/** Max latency between a transport publish and a React re-render. */
const FLUSH_INTERVAL_MS = 250;

type SlotComparableSnapshot = {
	slot: number;
	receivedAt: number;
};

/**
 * Pick the freshest snapshot across sources. Every transport carries a chain
 * slot, so the highest slot wins; equal slots break on most recent
 * `receivedAt`.
 */
const selectNewestSnapshot = <T extends SlotComparableSnapshot>(
	snapshots: T[]
): T | undefined => {
	if (snapshots.length === 0) return undefined;

	return snapshots.reduce((best, curr) => {
		if (curr.slot > best.slot) return curr;
		if (curr.slot < best.slot) return best;
		return curr.receivedAt >= best.receivedAt ? curr : best;
	});
};

type PerMarketPricesSources = {
	oracleBySource: Partial<Record<LivePriceSource, LiveOracleSnapshot>>;
	markBySource: Partial<Record<LivePriceSource, LiveMarkSnapshot>>;
	mmOracleBySource: Partial<Record<LivePriceSource, LiveMmOracleSnapshot>>;
};

/** Synchronous latest-value cache. Always fresh; never read by React directly. */
const pricesSourceCache = new Map<MarketKey, PerMarketPricesSources>();
const dirtyMarkets = new Set<MarketKey>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/** The per-market snapshot React consumers subscribe to. */
export type FlushedMarketSnapshot = {
	oracle?: LiveOracleSnapshot;
	mark?: BigNum;
	mmOracle?: LiveMmOracleSnapshot;
};

interface MarketPricesStore {
	snapshots: Record<MarketKey, FlushedMarketSnapshot>;
}

/**
 * Internal reactive store. Do not import outside this module — consume via
 * the hooks exported from ./index.ts.
 */
export const useMarketPricesStore = create<MarketPricesStore>(() => ({
	snapshots: {},
}));

const selectBestOracle = (
	entry: PerMarketPricesSources
): LiveOracleSnapshot | undefined =>
	selectNewestSnapshot(Object.values(entry.oracleBySource));

const selectBestMark = (entry: PerMarketPricesSources): BigNum | undefined => {
	// A zero mark is "no mark" (matches the legacy pricingDataToMarkPrice
	// behaviour of falling back to oracle when markPrice === 0).
	const marks = Object.values(entry.markBySource).filter((m) =>
		m.price.gtZero()
	);

	if (marks.length > 0) {
		// Highest chain slot wins (equal slots break on most recent receivedAt);
		// wall-clock recency is not consulted — slot is authoritative across
		// transports, same as oracle arbitration.
		return selectNewestSnapshot(marks)?.price;
	}

	return selectBestOracle(entry)?.price;
};

const selectBestMmOracle = (
	entry: PerMarketPricesSources
): LiveMmOracleSnapshot | undefined =>
	selectNewestSnapshot(Object.values(entry.mmOracleBySource));

/**
 * The single oracle price to show / trade against for a market: the freshest of
 * the market-maker oracle and the regular oracle, decided by chain slot.
 * `isMmOracle` reports which one won so consumers can pick the matching
 * slot-delay and confidence semantics (mmOracle carries its own confidence;
 * the regular oracle's confidence may need borrowing — see getLiveRawOracleData).
 */
export type LiveDisplayOracle = {
	price: BigNum;
	slot: number;
	confidence?: BN;
	isMmOracle: boolean;
};

/**
 * Arbitrate mmOracle vs regular oracle by slot. The mmOracle wins on a higher
 * slot and on a tie (`>=`), preserving the prior "prefer mmOracle when present"
 * intent while letting a genuinely fresher regular oracle take over. This is the
 * one place the oracle-vs-mmOracle choice is made; no consumer compares them.
 */
const arbitrateDisplayOracle = (
	oracle: LiveOracleSnapshot | undefined,
	mmOracle: LiveMmOracleSnapshot | undefined
): LiveDisplayOracle | undefined => {
	if (mmOracle && (!oracle || mmOracle.slot >= oracle.slot)) {
		return {
			price: mmOracle.price,
			slot: mmOracle.slot,
			confidence: mmOracle.confidence,
			isMmOracle: true,
		};
	}
	if (oracle) {
		return {
			price: oracle.price,
			slot: oracle.slot,
			confidence: oracle.confidence,
			isMmOracle: false,
		};
	}
	return undefined;
};

const flush = () => {
	if (dirtyMarkets.size === 0) return;

	const prevSnapshots = useMarketPricesStore.getState().snapshots;
	const changed: Record<MarketKey, FlushedMarketSnapshot> = {};
	let anyChanged = false;

	for (const key of dirtyMarkets) {
		const entry = pricesSourceCache.get(key);
		if (!entry) continue;

		const prev = prevSnapshots[key];
		const nextOracle = selectBestOracle(entry);
		const nextMark = selectBestMark(entry);
		const nextMmOracle = selectBestMmOracle(entry);

		// Keep previous object references when values are unchanged so
		// per-market Zustand selectors don't trigger useless re-renders. A
		// dimension is "unchanged" when both the previous and next values are
		// absent, or both present and equal — a value appearing or disappearing
		// counts as a change.
		const oracleUnchanged =
			prev?.oracle === undefined
				? nextOracle === undefined
				: nextOracle !== undefined &&
					prev.oracle.source === nextOracle.source &&
					prev.oracle.receivedAt === nextOracle.receivedAt;
		const markUnchanged =
			prev?.mark === undefined
				? nextMark === undefined
				: nextMark !== undefined && prev.mark.val.eq(nextMark.val);
		const mmOracleUnchanged =
			prev?.mmOracle === undefined
				? nextMmOracle === undefined
				: nextMmOracle !== undefined &&
					prev.mmOracle.source === nextMmOracle.source &&
					prev.mmOracle.receivedAt === nextMmOracle.receivedAt;

		if (oracleUnchanged && markUnchanged && mmOracleUnchanged) continue;

		// `prev` is undefined on a market's first flush; when a dimension is
		// "unchanged" there it's because both sides are absent, so `prev?.X`
		// correctly yields undefined rather than throwing.
		changed[key] = {
			oracle: oracleUnchanged ? prev?.oracle : nextOracle,
			mark: markUnchanged ? prev?.mark : nextMark,
			mmOracle: mmOracleUnchanged ? prev?.mmOracle : nextMmOracle,
		};
		anyChanged = true;
	}

	dirtyMarkets.clear();

	if (!anyChanged) return;

	useMarketPricesStore.setState((s) => ({
		snapshots: { ...s.snapshots, ...changed },
	}));
};

const scheduleFlush = () => {
	if (flushTimer !== null) return;
	flushTimer = setTimeout(() => {
		flushTimer = null;
		flush();
	}, FLUSH_INTERVAL_MS);
};

/**
 * THE write seam. Every transport adapter (pricing websocket, DLOB-derived,
 * RPC, fakes in tests) publishes through this one function; nothing else in
 * the app may write live oracle/mark state anywhere else.
 */
export const publishMarketLiveUpdate = (
	source: LivePriceSource,
	marketId: MarketId,
	update: MarketLiveUpdate
): void => {
	if (!marketId) return;
	if (!update.oracle && !update.mark && !update.mmOracle) return;

	const key = marketId.key;
	let entry = pricesSourceCache.get(key);
	if (!entry) {
		entry = { oracleBySource: {}, markBySource: {}, mmOracleBySource: {} };
		pricesSourceCache.set(key, entry);
	}

	const receivedAt = Date.now();

	if (update.oracle) {
		entry.oracleBySource[source] = { ...update.oracle, source, receivedAt };
	}
	if (update.mark) {
		entry.markBySource[source] = { ...update.mark, source, receivedAt };
	}
	if (update.mmOracle) {
		entry.mmOracleBySource[source] = { ...update.mmOracle, source, receivedAt };
	}

	dirtyMarkets.add(key);
	scheduleFlush();
};

/**
 * Imperative oracle read — synchronous and unthrottled (always freshest).
 * Without `source`: the best snapshot across transports (highest chain slot;
 * equal slots break on most recent). With `source`: that source's latest
 * snapshot only (e.g. 'rpc' when raw SDK OraclePriceData is required).
 */
export const getLiveOracleData = (
	marketId: MarketId | undefined,
	source?: LivePriceSource
): LiveOracleSnapshot | undefined => {
	if (!marketId) return undefined;
	const entry = pricesSourceCache.get(marketId.key);
	if (!entry) return undefined;
	return source ? entry.oracleBySource[source] : selectBestOracle(entry);
};

/**
 * Imperative mark price read — synchronous and unthrottled. Falls back to the
 * best oracle price when no non-zero mark exists.
 */
export const getLiveMarkPrice = (
	marketId: MarketId | undefined
): BigNum | undefined => {
	if (!marketId) return undefined;
	const entry = pricesSourceCache.get(marketId.key);
	if (!entry) return undefined;
	return selectBestMark(entry);
};

/**
 * Imperative mmOracle read — synchronous and unthrottled. Best across
 * transports (highest chain slot), or a single source's latest when `source`
 * is given. Only the DLOB transports publish an mmOracle.
 */
export const getLiveMmOracleData = (
	marketId: MarketId | undefined,
	source?: LivePriceSource
): LiveMmOracleSnapshot | undefined => {
	if (!marketId) return undefined;
	const entry = pricesSourceCache.get(marketId.key);
	if (!entry) return undefined;
	return source ? entry.mmOracleBySource[source] : selectBestMmOracle(entry);
};

export const toRawMmOracleData = (
	mmOracle: LiveMmOracleSnapshot | undefined
): MMOraclePriceData | undefined => {
	if (!mmOracle) return undefined;
	return {
		price: mmOracle.price.val,
		slot: new BN(mmOracle.slot),
		confidence: mmOracle.confidence ?? ZERO,
		hasSufficientNumberOfDataPoints: !!mmOracle.hasSufficientNumberOfDataPoints,
		isMMOracleActive: !!mmOracle.isMMOracleActive,
	};
};

/**
 * Imperative slot-arbitrated display oracle — synchronous and unthrottled. The
 * freshest of the market-maker and regular oracle (highest chain slot; tie
 * prefers mmOracle). Use for non-React reads (e.g. order-time diagnostics); the
 * reactive equivalent is `useLiveOracleForDisplay`.
 */
export const getLiveOracleForDisplay = (
	marketId: MarketId | undefined
): LiveDisplayOracle | undefined => {
	if (!marketId) return undefined;
	const entry = pricesSourceCache.get(marketId.key);
	if (!entry) return undefined;
	return arbitrateDisplayOracle(
		selectBestOracle(entry),
		selectBestMmOracle(entry)
	);
};

/**
 * Imperative raw-oracle read as SDK `OraclePriceData`. Prefers the transport's
 * untouched `raw` when present (rpc + DLOB carry it), otherwise reconstructs it
 * from the best snapshot's scaled fields. Replaces the store's
 * `getRawOracleDataForMarket`.
 *
 * When the freshest snapshot carries no detail fields (the pricing-ws transport
 * publishes only price + slot), the confidence/twap/maxPrice are borrowed from
 * the freshest snapshot that does carry a `raw` (rpc/DLOB). Price and slot stay
 * authoritative (from the freshest snapshot), but the confidence + index TWAP
 * consumers (e.g. the market header) keep real values instead of collapsing to
 * 0/undefined whenever a detail-less transport wins arbitration.
 */
export const getLiveRawOracleData = (
	marketId: MarketId | undefined
): OraclePriceData | undefined => {
	if (!marketId) return undefined;
	const entry = pricesSourceCache.get(marketId.key);
	if (!entry) return undefined;

	const snapshot = selectBestOracle(entry);
	if (!snapshot) return undefined;
	if (snapshot.raw) return snapshot.raw;

	const detail = selectNewestSnapshot(
		Object.values(entry.oracleBySource).filter((o) => o.raw)
	)?.raw;

	return {
		price: snapshot.price.val,
		slot: new BN(snapshot.slot),
		confidence: snapshot.confidence ?? detail?.confidence ?? new BN(0),
		hasSufficientNumberOfDataPoints:
			snapshot.hasSufficientNumberOfDataPoints ??
			detail?.hasSufficientNumberOfDataPoints ??
			false,
		twap: snapshot.twap?.val ?? detail?.twap,
		twapConfidence: snapshot.twapConfidence ?? detail?.twapConfidence,
		maxPrice: snapshot.maxPrice?.val ?? detail?.maxPrice,
	};
};

/**
 * Reactive raw-oracle read as SDK `OraclePriceData` (see `getLiveRawOracleData`).
 * Re-derives whenever the reactive oracle snapshot changes, so consumers of the
 * reconstructed raw (e.g. the header's confidence warning) update on the flush
 * cadence instead of piggy-backing on an unrelated re-render. The imperative
 * reconstruction still borrows detail fields from a raw-carrying source when the
 * winning transport ships none. Updates at most every FLUSH_INTERVAL_MS.
 */
export const useLiveRawOracleData = (
	marketId: MarketId | undefined
): OraclePriceData | undefined => {
	const oracle = useLiveOraclePrice(marketId);
	// `oracle` only changes identity when the winning snapshot changes — exactly
	// when the reconstruction could differ — so it's the right recompute trigger.
	return useMemo(() => getLiveRawOracleData(marketId), [marketId?.key, oracle]);
};

/**
 * Reactive oracle read. Updates at most every FLUSH_INTERVAL_MS.
 */
export const useLiveOraclePrice = (
	marketId: MarketId | undefined
): LiveOracleSnapshot | undefined =>
	useMarketPricesStore((s) =>
		marketId ? s.snapshots[marketId.key]?.oracle : undefined
	);

/**
 * Reactive mark price read. Updates at most every FLUSH_INTERVAL_MS.
 */
export const useLiveMarkPrice = (
	marketId: MarketId | undefined
): BigNum | undefined =>
	useMarketPricesStore((s) =>
		marketId ? s.snapshots[marketId.key]?.mark : undefined
	);

/**
 * Reactive mmOracle read. Updates at most every FLUSH_INTERVAL_MS.
 */
export const useLiveMmOracle = (
	marketId: MarketId | undefined
): LiveMmOracleSnapshot | undefined =>
	useMarketPricesStore((s) =>
		marketId ? s.snapshots[marketId.key]?.mmOracle : undefined
	);

/**
 * Reactive slot-arbitrated display oracle — the freshest of the market-maker
 * and regular oracle (see `getLiveOracleForDisplay`). Composes the two reactive
 * reads, which keep referential identity across flushes, so the memo only
 * recomputes when a value actually changes. Updates at most every
 * FLUSH_INTERVAL_MS.
 */
export const useLiveOracleForDisplay = (
	marketId: MarketId | undefined
): LiveDisplayOracle | undefined => {
	const oracle = useLiveOraclePrice(marketId);
	const mmOracle = useLiveMmOracle(marketId);
	return useMemo(
		() => arbitrateDisplayOracle(oracle, mmOracle),
		[oracle, mmOracle]
	);
};

/**
 * Reactive bulk read over many markets' flushed snapshots. Returns a map from
 * each requested market's key to its `{ oracle, mark, mmOracle }` snapshot
 * (markets with nothing published are omitted). Updates at most every
 * FLUSH_INTERVAL_MS and — because flush keeps each market's snapshot object
 * referentially stable when unchanged, and this selects with shallow equality —
 * only re-renders when one of the requested markets' snapshot actually changes.
 *
 * This is the stage-4 replacement for the essential-interval tick pattern list
 * consumers (FeaturedMarkets, MarketSelector, spot-balance sync) used to force a
 * ~1s recompute of imperative price reads. A bulk snapshot is crisper (250ms,
 * change-gated) and lets those consumers drop the tick.
 *
 * Pass a memoized `marketIds` array to avoid rebuilding the selector each render.
 */
export const useLiveMarketsSnapshot = (
	marketIds: (MarketId | undefined)[]
): Record<MarketKey, FlushedMarketSnapshot> =>
	useMarketPricesStore(
		useShallow((s) => {
			const out: Record<MarketKey, FlushedMarketSnapshot> = {};
			for (const marketId of marketIds) {
				if (!marketId) continue;
				const snapshot = s.snapshots[marketId.key];
				if (snapshot) out[marketId.key] = snapshot;
			}
			return out;
		})
	);

/** Test-only: wipe all module state (sync cache, dirty set, reactive store). */
export const __resetMarketLiveStateForTests = () => {
	pricesSourceCache.clear();
	dirtyMarkets.clear();
	if (flushTimer !== null) {
		clearTimeout(flushTimer);
		flushTimer = null;
	}
	useMarketPricesStore.setState({ snapshots: {} });
};
